import { AuthContext } from '@/context/AuthContext';
import { DeviceLayout, useDeviceLayout } from '@/hooks/useDeviceLayout';
import { numberToPastelRainbowColor } from '@/javascript/color-helper';
import { ProjectTrackItem } from '@/javascript/project-objects';
import { useDraggable } from '@dnd-kit/core';
import { Box, Card, Text } from '@mantine/core';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import MidiBlocks from '../midi-blocks';
import './Timeline.css';

export interface ItemRenderProps {
	trackIndex: number;
	item: ProjectTrackItem;
	tighten: boolean;
	selected: boolean;
	measureWidth: number;
	addItemToSelectedItems: (id: string) => void;
	removeItemFromSelectedItems: (id: string) => void;
	clearSelectedItems: () => void;
	startDraggingItems: (itemActionLeaderId: string, distanceX: number) => void;
	startTrimmingItemsLeft: (itemActionLeaderId: string) => void;
	startTrimmingItemsRight: (itemActionLeaderId: string) => void;
	chopItem: (event: { pageX: number }, id: string) => void;
	chopMode: boolean;
	notationView: boolean;
	isTutorialItem: boolean;
	height: number;
	isPhoneOrSmaller: boolean;
	isTabletOrSmaller: boolean;
}

const TimelineItem: React.FC<ItemRenderProps> = React.memo((props) => {
	const {
		trackIndex,
		item,
		tighten,
		selected,
		measureWidth,
		addItemToSelectedItems,
		removeItemFromSelectedItems,
		clearSelectedItems,
		startDraggingItems,
		startTrimmingItemsLeft,
		startTrimmingItemsRight,
		chopItem,
		chopMode,
		notationView,
		height,
		isPhoneOrSmaller,
		isTabletOrSmaller,
	} = props;

	const auth = useContext(AuthContext);
	if (auth === undefined) {
		throw new Error("Timeline items must be a child of AuthContext.");
	}

	const vexflowContainerRef = useRef<HTMLDivElement>(null);

	const chopMarkerRef = useRef<HTMLDivElement>(null);

	// Refs for performance checking (MIDI note mode)
	const lastChopModeRef = useRef<boolean | null>(null);
	const isMouseOverRef = useRef<boolean>(false);

	const deviceLayout = useDeviceLayout();
	const [hovering, setHovering] = useState(false);
	const [notationDrawn, setNotationDrawn] = useState(false);

	const [animateContentBar, setAnimateContentBar] = useState(false);
	const prevSelectedRef = useRef(selected);
	const [openContentBarHeight, setOpenContentBarHeight] = useState(isPhoneOrSmaller ? 16 : isTabletOrSmaller ? 18 : 20);
	const [contentBarHeight, setContentBarHeight] = useState(0);
	useEffect(() => {
		const openContentBarHeight = isPhoneOrSmaller ? 16 : isTabletOrSmaller ? 18 : 20;
		setOpenContentBarHeight(openContentBarHeight);
		setContentBarHeight(selected ? openContentBarHeight : 0);
		if (prevSelectedRef.current !== selected) {
			prevSelectedRef.current = selected;
			// Enable animation for this select/deselect transition
			setAnimateContentBar(true);
			const timeoutId = window.setTimeout(() => {
				setAnimateContentBar(false);
			}, 105); // a bit longer than 0.1s

			return () => window.clearTimeout(timeoutId);
		}
	}, [selected, isPhoneOrSmaller, isTabletOrSmaller]);

	const leftHandleDraggable = useDraggable({
		id: `timeline-item-left-${item.id}`,
		data: { type: 'timeline-item', itemId: item.id, mode: 'trim-left' },
	});

	const rightHandleDraggable = useDraggable({
		id: `timeline-item-right-${item.id}`,
		data: { type: 'timeline-item', itemId: item.id, mode: 'trim-right' },
	});

	const bodyDraggable = useDraggable({
		id: `timeline-item-${item.id}`,
		data: { type: 'timeline-item', itemId: item.id, mode: 'move' },
	});

	const handleClick = useCallback(
		(event: { pageX: number; stopPropagation: () => void }) => {
			event.stopPropagation();
			if (chopMode) {
				chopItem(event, item.id);
			}
		},
		[chopMode, item, selected, chopItem, removeItemFromSelectedItems, addItemToSelectedItems]
	);

	const handleMouseMove = useCallback(
		(event: { clientX: number; currentTarget: { getBoundingClientRect: () => { left: number } } }) => {
			const cursorX = event.clientX - event.currentTarget.getBoundingClientRect().left;
			const measure = Math.round(cursorX / measureWidth);
			if (measure > 0 && measure < item.getTrimmedLength()) {
				isMouseOverRef.current = true;
				if (chopMarkerRef.current) {
					chopMarkerRef.current.style.setProperty('left', `${measure * measureWidth}px`);
					if (chopMode) {
						chopMarkerRef.current.style.setProperty('display', 'block');
					}
				}
			} else {
				isMouseOverRef.current = false;
				chopMarkerRef.current?.style.setProperty('display', 'none');
			}
		},
		[measureWidth, chopMode, item]
	);

	const handleMouseLeave = useCallback(() => {
		isMouseOverRef.current = false;
		chopMarkerRef.current?.style.setProperty('display', 'none');
		setHovering(false);
	}, []);

	const handleMouseEnter = useCallback(() => {
		setHovering(true);
	}, []);

	const handleLeftHandlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		event.stopPropagation();
		if (chopMode) {
			return;
		}
		if (!selected) {
			clearSelectedItems();
			addItemToSelectedItems(item.id);
		}
		startTrimmingItemsLeft(item.id);
		leftHandleDraggable.listeners?.onPointerDown?.(event);
	}, [chopMode, selected, clearSelectedItems, addItemToSelectedItems, item.id, startTrimmingItemsLeft, leftHandleDraggable.listeners]);

	const handleRightHandlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		event.stopPropagation();
		if (chopMode) {
			return;
		}
		if (!selected) {
			clearSelectedItems();
			addItemToSelectedItems(item.id);
		}
		startTrimmingItemsRight(item.id);
		rightHandleDraggable.listeners?.onPointerDown?.(event);
	}, [chopMode, selected, clearSelectedItems, addItemToSelectedItems, item.id, startTrimmingItemsRight, rightHandleDraggable.listeners]);

	const handleBodyPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
		event.stopPropagation();
		if (chopMode) {
			return;
		}
		if (!selected) {
			clearSelectedItems();
			addItemToSelectedItems(item.id);
		}
		const xDistance = event.clientX - event.currentTarget.getBoundingClientRect().left;
		startDraggingItems(item.id, xDistance);
		bodyDraggable.listeners?.onPointerDown?.(event);
	}, [chopMode, selected, clearSelectedItems, addItemToSelectedItems, item.id, startDraggingItems, bodyDraggable.listeners]);

	useEffect(() => {
		if (chopMode !== lastChopModeRef.current) {
			lastChopModeRef.current = chopMode;
			if (chopMode && isMouseOverRef.current) {
				chopMarkerRef.current?.style.setProperty('display', 'block');
			} else {
				chopMarkerRef.current?.style.setProperty('display', 'none');
			}
		}
	}, [chopMode]);

	// --- VexFlow SVG Rendering for Notation View ---
	const drawVexFlowSVG = useCallback(() => {
		const container = vexflowContainerRef.current;
		if (!container) return;
		// Render VexFlow using the updated SVG method.
		item.getSection().renderVexFlow(item.getInstrument(), container, measureWidth);
		setNotationDrawn(true);
	}, [item, measureWidth]);

	useEffect(() => {
		if (notationView && !notationDrawn) {
			drawVexFlowSVG();
		}
	}, [notationView, drawVexFlowSVG]);

	useEffect(() => {
		if (props.isTutorialItem && auth.beginnerTourActive && auth.currentTourStep === 3) {
			auth.jumpToTourStep(4);
		}
	}, [auth.beginnerTourActive, auth.currentTourStep, auth.jumpToTourStep, props.isTutorialItem]);

	const itemHeight = height * 0.98; // 98% of the full height
	// Styles and layout values
	const handleWidth = Math.min(Math.pow(measureWidth, 1.05) * 0.2, measureWidth / 3);
	const color = useMemo(() => numberToPastelRainbowColor(trackIndex), [trackIndex]);
	const useHoveringStyle = deviceLayout.deviceLayout === DeviceLayout.Desktop && hovering;
	const unselectedColorModifier = useHoveringStyle ? "3" : "2";

	return (
		<Box
			style={{
				height: itemHeight,
				position: "absolute",
				display: "flex",
				justifyContent: "space-between",
				alignItems: "stretch",
				left: item.getStartMeasure() * measureWidth + item.getTrimStart() * measureWidth,
				width: `${item.getTrimmedLength() * measureWidth}px`,
				pointerEvents: item.ghosted ? 'none' : 'unset',
				visibility: item.ghosted ? 'hidden' : 'visible',
			}}
			data-section-id={item.getSection().id}
			data-ghost-section={item.ghosted ? item.getSection().id : undefined}
			onClick={handleClick}
			onMouseDown={(e) => e.stopPropagation()}
			onMouseMove={handleMouseMove}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<Card
				bg={item.ghosted ? "gray.4" : selected ? "darkGreen.5" : `${color}.${unselectedColorModifier}`}
				radius={tighten ? "xs" : (tighten || selected ? "sm" : useHoveringStyle ? "md" : "lg")}
				style={{
					transition: 'border-radius 0.1s ease',
					pointerEvents: "none",
					position: "absolute",
					display: "flex",
					flexDirection: "column",
					alignItems: "stretch",
					minWidth: 0,
					outline: "1px solid #1e1e1e",
					left: 0,
					right: 0,
					opacity: item.ghosted ? 0.8 : 1,
				}}
				padding={0}
			>
				<Box
					style={{
						height: contentBarHeight,
						transition: animateContentBar ? 'height 0.1s ease' : 'none',
						borderBottom: "1px solid #1e1e1e"
					}}
					bg={selected ? `darkGreen.2` : "white"}
				>
					<Text
						style={{
							position: "absolute",
							left: 15,
							right: 15,
							fontSize: isPhoneOrSmaller ? 11 : isTabletOrSmaller ? 13 : 14,
							textAlignLast: "left",
							color: "black",
							display: "block",
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
							maxWidth: "100%",
							height: contentBarHeight,
							transition: animateContentBar ? 'height 0.1s ease' : 'none',
						}}
					>
						{item.getTitle()}
					</Text>
				</Box>
				<Box
					style={{
						position: 'relative',
						height: itemHeight - contentBarHeight,
						transition: animateContentBar ? 'height 0.1s ease' : 'none',
					}}
				>
					{/* MIDI Blocks: Always mounted, hidden when notationView is true */}
					<div
						style={{
							width: measureWidth * item.getSection().getLength(),
							height: "100%",
							position: "absolute",
							left: -measureWidth * item.getTrimStart(),
							display: notationView ? 'none' : 'block',
						}}
					>
						<MidiBlocks key={item.id} section={item.getSection()} />
					</div>

					{/* VexFlow Notation: Always mounted, hidden when notationView is false */}
					<div
						style={{
							width: measureWidth * item.getSection().getLength(),
							height: "100%",
							position: "absolute",
							left: -measureWidth * item.getTrimStart(),
							display: notationView ? 'block' : 'none',
							top: openContentBarHeight - contentBarHeight,
							transition: animateContentBar ? 'top 0.1s ease' : 'none',
						}}
					>
						<div key="vexflow" ref={vexflowContainerRef} style={{ width: "100%", height: "100%" }} />
					</div>
				</Box>
			</Card>
			<div
				style={{
					height: "100%",
					flex: "0 0 auto",
					cursor: "ew-resize",
					width: handleWidth,
					minWidth: 0,
					zIndex: 0,
				}}
			>
				<div
					style={{
						height: "100%",
						width: "100%",
						minWidth: 0,
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						visibility: useHoveringStyle || selected ? "visible" : "hidden",
					}}
					ref={leftHandleDraggable.setNodeRef}
					{...leftHandleDraggable.attributes}
					onPointerDown={handleLeftHandlePointerDown}
				>
					<Box
						bg={selected ? "black" : "blue"}
						style={{
							width: 8,
							height: 8,
							pointerEvents: "none",
							outline: "1px solid #1e1e1e",
						}}
					/>
					<Box
						bg={selected ? "black" : "blue"}
						style={{
							width: 8,
							height: 8,
							pointerEvents: "none",
							outline: "1px solid #1e1e1e",
						}}
					/>
				</div>
			</div>
			<div
				className={props.isTutorialItem ? "beginner-tour-item" : ""}
				style={{
					flex: 1,
					cursor: chopMode ? "col-resize" : selected ? "grab" : "pointer",
					alignSelf: "stretch",
				}}
				ref={bodyDraggable.setNodeRef}
				{...bodyDraggable.attributes}
				onPointerDown={handleBodyPointerDown}
			/>
			<div
				ref={chopMarkerRef}
				style={{
					position: "absolute",
					top: 0,
					bottom: 0,
					width: 0,
					outline: "1px dashed #1e1e1e",
					pointerEvents: "none"
				}}
			/>
			<div
				style={{
					height: "100%",
					flex: "0 0 auto",
					cursor: "ew-resize",
					width: handleWidth,
					minWidth: 0,
					zIndex: 0,
				}}
			>
				<div
					style={{
						height: "100%",
						width: "100%",
						minWidth: 0,
						display: "flex",
						flexDirection: "column",
						justifyContent: "space-between",
						alignItems: "flex-end",
						visibility: useHoveringStyle || selected ? "visible" : "hidden",
					}}
					ref={rightHandleDraggable.setNodeRef}
					{...rightHandleDraggable.attributes}
					onPointerDown={handleRightHandlePointerDown}
				>
					<Box
						bg={selected ? "black" : "blue"}
						style={{
							width: 8,
							height: 8,
							pointerEvents: "none",
							outline: "1px solid #1e1e1e",
						}}
					/>
					<Box
						bg={selected ? "black" : "blue"}
						style={{
							width: 8,
							height: 8,
							pointerEvents: "none",
							outline: "1px solid #1e1e1e",
						}}
					/>
				</div>
			</div>
		</Box>
	);
}, (prevProps, nextProps) => {
	const areEqual = prevProps.height === nextProps.height &&
		prevProps.measureWidth === nextProps.measureWidth &&
		prevProps.tighten === nextProps.tighten &&
		prevProps.selected === nextProps.selected &&
		prevProps.trackIndex === nextProps.trackIndex &&
		prevProps.chopMode === nextProps.chopMode &&
		prevProps.notationView === nextProps.notationView &&
		prevProps.item.getHash() === nextProps.item.getHash() &&
		prevProps.isPhoneOrSmaller === nextProps.isPhoneOrSmaller &&
		prevProps.isTabletOrSmaller === nextProps.isTabletOrSmaller;
	return areEqual;
});

export default TimelineItem;
